import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRY_CODES, countryCodeToFlag } from '@draughts/shared';
import { countryName } from '../../utils/country';

interface CountrySelectProps {
  /** Selected ISO 3166-1 alpha-2 code, or null/'' when unset. */
  value: string | null | undefined;
  onChange: (code: string | null) => void;
  className?: string;
  id?: string;
  'aria-label'?: string;
  /** When true, includes a blank "not set" option at the top. */
  allowEmpty?: boolean;
}

/**
 * Native country <select> following the LanguageSwitcher pattern. Options are
 * sorted by localized name (via Intl.DisplayNames + Intl.Collator) for the
 * current UI language; each option shows the flag emoji + localized name.
 */
export function CountrySelect({
  value,
  onChange,
  className = '',
  id,
  allowEmpty = true,
  ...rest
}: CountrySelectProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const options = useMemo(() => {
    const collator = new Intl.Collator(locale);
    return COUNTRY_CODES
      .map((code) => ({ code, name: countryName(code, locale), flag: countryCodeToFlag(code) }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [locale]);

  return (
    <select
      id={id}
      className={`country-select ${className}`.trim()}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      aria-label={rest['aria-label'] ?? t('profile.country', 'Country')}
    >
      {allowEmpty && <option value="">🌍 {t('profile.countryNone', '—')}</option>}
      {options.map((o) => (
        <option key={o.code} value={o.code}>
          {o.flag} {o.name}
        </option>
      ))}
    </select>
  );
}
